<!-- Ported from the Polaris kit: src/product/components/SearchSelect.vue.
     Classes translated to Tailwind 3 by rebranding_frontend/port-kit-component.mjs;
     strings moved to i18n by hand. Change the design in the kit first.
     Hand changes (phase P, 28/09): the value can be a number too (the woot's ids: agents, inboxes, teams);
     "Choose", "none", "Search", "Nothing with that name." and "Chosen" go through i18n (POLARIS.SELECT). -->
<script setup>
// One value out of a list that is long enough to need a search: a button that shows the value and opens a searchable list, with the
// chosen one marked. OptionSelect is for a short list; this is for people, tags, countries. Built on the registry popover and command.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { Checkmark16, ChevronDown16 } from '@carbon/icons-vue';
import { Button } from '../ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

const props = defineProps({
  options: { type: Array, required: true }, // ['Open', 'Closed'] or [{ value, label }]
  placeholder: { type: String, default: '' }, // default: "Choose"
  searchable: { type: Boolean, default: true },
  disabled: Boolean,
  label: { type: String, default: '' }, // the accessible name of the button, "Assignee"
});
defineOptions({ inheritAttrs: false });
const model = defineModel({ type: [String, Number], default: '' });
const { t } = useI18n();
const items = computed(() =>
  props.options.map(o => (typeof o === 'object' ? o : { value: o, label: o }))
);
const chosen = computed(() => items.value.find(o => o.value === model.value));
const open = ref(false);
const pick = value => {
  model.value = value;
  open.value = false;
};
</script>

<template>
  <Popover v-model:open="open">
    <PopoverTrigger as-child>
      <Button
        v-bind="$attrs"
        type="button"
        variant="outline"
        :disabled="disabled"
        :aria-label="
          label
            ? t('POLARIS.SELECT.LABELED', {
                label,
                value: chosen?.label ?? t('POLARIS.SELECT.NONE'),
              })
            : undefined
        "
        class="w-full justify-between font-normal"
        data-search-select
        :data-value="model"
      >
        <span
          class="truncate"
          :class="[chosen ? '' : 'text-polaris-muted-foreground']"
        >
          {{ chosen?.label ?? (placeholder || t('POLARIS.SELECT.CHOOSE')) }}
        </span>
        <ChevronDown16 class="opacity-60" aria-hidden="true" />
      </Button>
    </PopoverTrigger>
    <PopoverContent
      class="w-[var(--reka-popover-trigger-width)] min-w-52 p-0"
      align="start"
    >
      <Command>
        <CommandInput
          v-if="searchable"
          :placeholder="t('POLARIS.SELECT.SEARCH')"
        />
        <CommandList>
          <CommandEmpty>{{ t('POLARIS.SELECT.NO_MATCH') }}</CommandEmpty>
          <CommandGroup>
            <CommandItem
              v-for="o in items"
              :key="o.value"
              :value="o.label"
              @select="pick(o.value)"
            >
              {{ o.label }}
              <Checkmark16
                v-if="o.value === model"
                class="ml-auto"
                :aria-label="t('POLARIS.SELECT.CHOSEN')"
              />
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </Command>
    </PopoverContent>
  </Popover>
</template>
